import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Camera, FileText, Loader2, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/operations`;
const CLICK_DELAY_MS = 260;
const PHOTO_ACCEPT = "image/jpeg,image/png,image/webp,image/heic,image/heif";
const DOCUMENT_ACCEPT =
  ".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,image/jpeg,image/png,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv,text/plain";

/**
 * One `<td>` per component. Mode picks whether it's the Photos cell
 * (single-tap → gallery, double-tap → upload) or the Documents cell
 * (single-tap → docs dialog with an "Upload document" button inside).
 * Attachments live on the payment record via the payroll_attachments
 * router; the shared photo_storage adapter handles R2 / local FS.
 */
export function PaymentAttachmentCell({ paymentId, mode, canUpload = true, cellProps }) {
  const isPhotos = mode === "photos";
  const kind = isPhotos ? "photos" : "documents";
  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const clickTimer = useRef(null);
  const inputRef = useRef(null);

  const loadCount = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/payroll/payments/${paymentId}/${kind}/count`);
      setCount(r.data?.count || 0);
    } catch { /* silent */ }
  }, [paymentId, kind]);

  const loadItems = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/payroll/payments/${paymentId}/${kind}`);
      setItems(r.data?.[kind] || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Could not load ${kind}.`);
    }
  }, [paymentId, kind]);

  useEffect(() => { loadCount(); }, [loadCount]);
  useEffect(() => () => { if (clickTimer.current) clearTimeout(clickTimer.current); }, []);
  useEffect(() => { if (open) loadItems(); }, [open, loadItems]);

  const uploadFiles = async (files) => {
    setUploading(true);
    try {
      for (const file of files) {
        const form = new FormData();
        form.append("file", file);
        await axios.post(`${API}/payroll/payments/${paymentId}/${kind}`, form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      toast.success(`Uploaded ${files.length} ${isPhotos ? "photo" : "document"}${files.length > 1 ? "s" : ""}.`);
      await loadCount();
      if (open) await loadItems();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const onInput = (event) => {
    const files = Array.from(event.target.files || []);
    if (inputRef.current) inputRef.current.value = "";
    if (files.length) uploadFiles(files);
  };

  const handlePhotoClick = () => {
    if (uploading) return;
    if (clickTimer.current) {
      clearTimeout(clickTimer.current);
      clickTimer.current = null;
      if (canUpload) inputRef.current?.click();
      else toast.error("You do not have permission to upload.");
      return;
    }
    clickTimer.current = setTimeout(() => {
      clickTimer.current = null;
      setOpen(true);
    }, CLICK_DELAY_MS);
  };

  const removeItem = async (itemId) => {
    if (!canUpload) return;
    try {
      await axios.delete(`${API}/payroll/payments/${paymentId}/${kind}/${itemId}`);
      await loadCount();
      await loadItems();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed.");
    }
  };

  const tdProps = cellProps ? cellProps({ rowKey: paymentId, colKey: kind }) : {};

  return (
    <Fragment>
      <td
        {...tdProps}
        data-testid={`payment-${kind}-cell-${paymentId}`}
        style={{ textAlign: "center", ...(tdProps.style || {}) }}
      >
        <input
          accept={isPhotos ? PHOTO_ACCEPT : DOCUMENT_ACCEPT}
          data-testid={`payment-${kind}-input-${paymentId}`}
          multiple
          onChange={onInput}
          ref={inputRef}
          style={{ display: "none" }}
          type="file"
        />
        <div style={{ display: "inline-flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
          <button
            className="table-edit-button"
            data-testid={`payment-${kind}-btn-${paymentId}`}
            disabled={uploading}
            onClick={isPhotos ? handlePhotoClick : () => setOpen(true)}
            style={{ display: "inline-flex", alignItems: "center", gap: "0.3rem", minWidth: 52 }}
            type="button"
          >
            {uploading ? <Loader2 size={13} /> : isPhotos ? <Camera size={13} /> : <FileText size={13} />}
            <span>{uploading ? "…" : count}</span>
          </button>
          {isPhotos && (
            <small style={{ color: "#94a3b8", fontSize: "0.65rem", lineHeight: 1 }}>
              Double-tap to upload
            </small>
          )}
        </div>
      </td>

      <Dialog
        open={open}
        onOpenChange={(v) => { if (!v) { setOpen(false); loadCount(); } }}
      >
        <DialogContent data-testid={`payment-${kind}-dialog-${paymentId}`}>
          <DialogHeader>
            <DialogTitle>{isPhotos ? "Payment photos" : "Payment documents"}</DialogTitle>
          </DialogHeader>
          {!isPhotos && canUpload && (
            <div style={{ marginBottom: 10 }}>
              <button
                className="invoice-primary-button"
                data-testid={`payment-documents-upload-inside-${paymentId}`}
                disabled={uploading}
                onClick={() => inputRef.current?.click()}
                style={{ display: "inline-flex", alignItems: "center", gap: "0.35rem" }}
                type="button"
              >
                <Upload size={13} /> Upload document
              </button>
            </div>
          )}
          {isPhotos ? (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 10 }}>
              {items.length === 0 && (
                <p data-testid={`payment-photos-empty-${paymentId}`} style={{ color: "#64748b" }}>
                  No photos yet. Double-tap the Photos cell to upload.
                </p>
              )}
              {items.map((p) => (
                <div key={p.id} data-testid={`payment-photo-${p.id}`} style={{ position: "relative", border: "1px solid #e5e7eb", borderRadius: 8, overflow: "hidden" }}>
                  <img alt={p.filename} src={p.url} style={{ width: "100%", height: 120, objectFit: "cover", display: "block" }} />
                  <div style={{ padding: 6, fontSize: 11, color: "#475569" }}>
                    <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={p.filename}>{p.filename}</div>
                    <div style={{ color: "#94a3b8", fontSize: 10 }}>{p.uploaded_by_name}</div>
                  </div>
                  {canUpload && (
                    <button
                      data-testid={`payment-photo-delete-${p.id}`}
                      onClick={() => removeItem(p.id)}
                      style={{ position: "absolute", top: 4, right: 4, background: "rgba(255,255,255,0.9)", border: "1px solid #fecaca", borderRadius: 999, padding: 3, cursor: "pointer", color: "#b91c1c" }}
                      title="Delete photo"
                      type="button"
                    >
                      <Trash2 size={12} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {items.length === 0 && (
                <p data-testid={`payment-documents-empty-${paymentId}`} style={{ color: "#64748b" }}>
                  No documents yet. Use the button above to upload.
                </p>
              )}
              {items.map((d) => (
                <div key={d.id} data-testid={`payment-doc-${d.id}`} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", border: "1px solid #e5e7eb", borderRadius: 8 }}>
                  <FileText size={14} style={{ color: "#475569" }} />
                  <a href={d.url} rel="noopener noreferrer" style={{ color: "#0f172a", textDecoration: "none", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} target="_blank" title={d.filename}>
                    {d.filename}
                  </a>
                  <span style={{ color: "#94a3b8", fontSize: 11 }}>{d.uploaded_by_name}</span>
                  {canUpload && (
                    <button
                      data-testid={`payment-doc-delete-${d.id}`}
                      onClick={() => removeItem(d.id)}
                      style={{ background: "transparent", border: "1px solid #fecaca", borderRadius: 999, padding: 3, cursor: "pointer", color: "#b91c1c" }}
                      title="Delete document"
                      type="button"
                    >
                      <Trash2 size={12} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Fragment>
  );
}
